import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import ApplyRCModal from './ApplyRCModal';
import api from '../services/api';

const RouteReceptionTab = ({ session, date, balance }) => {
    const [dailyIncome, setDailyIncome] = useState(null);
    const [loading, setLoading] = useState(false);
    // Fix: Use standard Colombia date string per SKILL.md
    const getColombiaDate = () => {
        return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
    };

    const [selectedDate, setSelectedDate] = useState(date || getColombiaDate());
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showRCModal, setShowRCModal] = useState(false);

    useEffect(() => {
        if (date) {
            setSelectedDate(date);
        }
    }, [date]);

    useEffect(() => {
        fetchDailyIncome();
    }, [selectedDate, session?.opened_at]);

    const fetchDailyIncome = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();

            if (session?.opened_at) {
                // Pass exact session timestamp for alignment with getCashBalance
                params.set('opened_at', session.opened_at);
            } else {
                params.set('date', selectedDate);
            }

            const response = await api.get(`/cash-box/daily-income?${params}`);
            setDailyIncome(response.data);
        } catch (error) {
            console.error('Error fetching daily income:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    const formatTime = (datetime) => {
        if (!datetime) return '-';
        return new Date(datetime).toLocaleTimeString('es-CO', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const safeRender = (value) => {
        if (typeof value === 'object' && value !== null) {
            console.warn('⚠️ Intentando renderizar objeto:', value);
            return JSON.stringify(value);
        }
        return value;
    };

    const formatPaymentLabel = (method, context = 'general') => {
        if (!method) return 'Desconocido';
        const m = method.toLowerCase();

        // Standardize long technical names
        if (m === 'efectivo_ruta_domicilio' || m === 'efectivo_ruta') return 'Efec. Ruta';

        // Context-specific overrides
        if (m === 'efectivo') {
            if (context === 'route' || context === 'orphans') return 'Efec. Ruta';
            return 'Efectivo';
        }

        return safeRender(method);
    };

    // Debug log
    if (dailyIncome) {
        console.log('Daily Income Data (FixApplied):', dailyIncome);
    }

    if (loading && !dailyIncome) {
        return <div className="text-center py-8">Cargando ingresos...</div>;
    }

    // ============================================================
    // SEGMENTATION LOGIC — MUTUALLY EXCLUSIVE PILLARS
    // [FIX April 20, 2026] Removed credit-customer split from bodega.
    // Each order appears in EXACTLY ONE pilar to prevent inflated totals:
    //   Pilar 1 (Bodega) = ALL warehouse cash_register orders
    //   Pilar 2 (Ruta)   = ALL route cash_register orders  
    //   Pilar 3 (Cartera) = ONLY portfolio_payments NOT in cash_register
    // ============================================================
    const regularSales = dailyIncome?.warehouse_pickups || [];
    const portfolioPayments = dailyIncome?.portfolio_payments || [];

    // Pilar 3: Only unique portfolio payments (backend already excludes cash_register duplicates)
    const allCreditCustomerMovements = portfolioPayments
        .map(p => ({ ...p, movementType: 'PORTFOLIO', is_credit_customer: true }))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return (
        <div className="space-y-4">
            {/* Date Selector (Only if not controlled by parent) */}
            {!date && !session && (
                <div className="flex items-center gap-4 mb-4">
                    <label className="text-sm font-medium text-gray-700">Fecha:</label>
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="border border-gray-300 rounded px-3 py-2"
                    />
                </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-emerald-50 border-emerald-100">
                    <CardContent className="pt-4">
                        <div className="text-xs text-emerald-600 font-bold uppercase mb-1">💵 Ingresos de la Sesión</div>
                        <div className="text-2xl font-bold text-emerald-700">
                            {formatCurrency(balance?.total_income || dailyIncome?.summary?.total_income || 0)}
                        </div>
                        <div className="mt-2 pt-2 border-t border-emerald-200 grid grid-cols-2 gap-4">
                            <div>
                                <div className="text-[10px] text-emerald-500 font-bold uppercase">📦 Ventas Hoy</div>
                                <div className="text-sm font-bold text-emerald-700">{formatCurrency(dailyIncome?.summary?.total_cash_sales || 0)}</div>
                            </div>
                            <div>
                                <div className="text-[10px] text-indigo-500 font-bold uppercase underline">💼 Cartera</div>
                                <div className="text-sm font-bold text-indigo-700 font-mono tracking-tighter">{formatCurrency(dailyIncome?.summary?.total_cash_portfolio || 0)}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="pt-4">
                        <div className="text-xs text-gray-500 font-bold uppercase mb-1">🏦 Depósito Siigo</div>
                        <div className="text-2xl font-bold text-gray-900">
                            {formatCurrency(dailyIncome?.summary?.breakdown?.siigo || 0)}
                        </div>
                        <div className="text-[10px] text-gray-400 mt-1">Facturación oficial del día</div>
                    </CardContent>
                </Card>

                <Card className="bg-purple-50 border-purple-100">
                    <CardContent className="pt-4">
                        <div className="text-xs text-purple-600 font-bold uppercase mb-1">💜 Recaudo INT</div>
                        <div className="text-2xl font-bold text-purple-700">
                            {formatCurrency(dailyIncome?.summary?.breakdown?.int || 0)}
                        </div>
                        <div className="text-[10px] text-purple-500 mt-1">Ventas internas (locales)</div>
                    </CardContent>
                </Card>

                <Card className="bg-orange-50 border-orange-100">
                    <CardContent className="pt-4">
                        <div className="text-xs text-orange-600 font-bold uppercase mb-1">⏳ RCs Pendientes</div>
                        <div className="text-2xl font-bold text-orange-700">
                            {dailyIncome?.summary?.pending_rcs || 0}
                        </div>
                        <div className="text-[10px] text-orange-500 mt-1">Pendientes por legalizar</div>
                    </CardContent>
                </Card>
            </div>

            {/* Routes Section removed — all orders now come from c            {/* PILAR 3: Consolidated Credit Customer Section — MOVED TO TOP AS PER USER REQUEST */}
            {allCreditCustomerMovements.length > 0 && (
                <Card className="mt-2 border-indigo-200 shadow-sm">
                    <CardHeader className="bg-indigo-50 pb-2 border-b border-indigo-100">
                        <CardTitle className="text-lg text-indigo-800 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span>💼 PILAR 3: Movimientos Clientes Crédito (Cartera)</span>
                                <span className="text-xs font-normal text-indigo-500 bg-white px-3 py-1 rounded-full shadow-sm border border-indigo-100 italic">
                                    {allCreditCustomerMovements.length} movimientos
                                </span>
                            </div>
                            <div className="flex flex-col items-end">
                                <span className="text-xs text-indigo-500 uppercase font-bold tracking-wider">Total Cartera</span>
                                <span className="text-xl font-black text-indigo-900 leading-none">
                                    {formatCurrency(allCreditCustomerMovements.reduce((s, m) => s + parseFloat(m.amount || 0), 0))}
                                </span>
                            </div>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-indigo-50/50 text-indigo-700 border-b">
                                    <tr>
                                        <th className="px-4 py-2 font-medium w-12 text-center">#</th>
                                        <th className="px-4 py-2 font-medium">Tipo</th>
                                        <th className="px-4 py-2 font-medium">Documento / Ref</th>
                                        <th className="px-4 py-2 font-medium">Cliente</th>
                                        <th className="px-4 py-2 font-medium text-right">Monto</th>
                                        <th className="px-4 py-2 font-medium">Gestor</th>
                                        <th className="px-4 py-2 font-medium text-center">Estado RC</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {allCreditCustomerMovements.map((m, idx) => {
                                        const rcName = m.rc_number || m.reference || null;
                                        const hasRc = m.has_rc || !!rcName;

                                        return (
                                            <tr key={idx} className="hover:bg-indigo-50/30 transition-colors">
                                                <td className="px-4 py-3 align-top text-center text-gray-400 font-mono text-xs">
                                                    {(idx + 1).toString().padStart(2, '0')}
                                                </td>
                                                <td className="px-4 py-3 align-top">
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[9px] font-bold bg-purple-100 text-purple-800 border border-purple-200">
                                                        💰 ABONO
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 align-top">
                                                    <div className="font-bold text-gray-900">{m.order_number || m.reference || '-'}</div>
                                                    <div className="text-[10px] text-indigo-500 font-medium">
                                                        {new Date(m.created_at).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 align-top font-medium text-gray-700">
                                                    {m.customer_name}
                                                </td>
                                                <td className="px-4 py-3 align-top text-right">
                                                    <div className="font-extrabold text-indigo-700">{formatCurrency(m.amount)}</div>
                                                </td>
                                                <td className="px-4 py-3 align-top text-[11px] text-gray-600">
                                                    👤 {m.managed_by_name || m.managed_by_username || 'Sistema'}
                                                </td>
                                                <td className="px-4 py-3 align-top text-center">
                                                    {hasRc ? (
                                                        <div className="flex flex-col items-center gap-1">
                                                            <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-800 uppercase border border-emerald-200 shadow-sm">Legalizado</span>
                                                            <span className="text-[10px] font-medium text-emerald-600">{rcName}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800 uppercase border border-amber-200">Pendiente RC</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* PILAR 1: Warehouse Pickups Section (Regular Sales) */}
            {regularSales.length > 0 && (
                <Card className="mt-4 shadow-sm">
                    <CardHeader className="bg-gray-50 pb-2 border-b">
                        <CardTitle className="text-lg flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span>📦 PILAR 1: Recaudos de Bodega (Ventas Contado)</span>
                                <span className="text-xs font-normal text-gray-500 bg-white px-2 py-1 rounded border">
                                    {regularSales.length} pedidos
                                </span>
                            </div>
                            <div className="flex flex-col items-end">
                                <span className="text-xs text-gray-500 uppercase font-bold tracking-wider">Total Bodega</span>
                                <span className="text-xl font-black text-gray-900 leading-none">
                                    {formatCurrency(regularSales.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0))}
                                </span>
                            </div>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-100 text-gray-600 border-b">
                                    <tr>
                                        <th className="px-4 py-2 font-medium w-12 text-center">#</th>
                                        <th className="px-4 py-2 font-medium">Pedido / INT</th>
                                        <th className="px-4 py-2 font-medium">Cliente</th>
                                        <th className="px-4 py-2 font-medium">Fechas</th>
                                        <th className="px-4 py-2 font-medium text-right">Valores</th>
                                        <th className="px-4 py-2 font-medium">Entregado Por</th>
                                        <th className="px-4 py-2 font-medium text-center">Estado RC</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {regularSales.map((pickup, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-3 align-top text-center text-gray-500 font-medium text-xs">
                                                {idx + 1}
                                            </td>
                                            <td className="px-4 py-3 align-top">
                                                <div className="font-bold text-gray-900">{pickup.order_number}</div>
                                                {pickup.int_order_number && (
                                                    <div className="text-[10px] text-purple-600 font-medium mt-0.5">
                                                        🔗 {pickup.int_order_number}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 align-top">
                                                <div className="font-medium text-gray-900">{pickup.customer_name}</div>
                                            </td>
                                            <td className="px-4 py-3 align-top text-[11px] text-gray-600">
                                                <div>Fac: {new Date(pickup.created_at).toLocaleDateString()}</div>
                                                <div className="font-medium text-orange-700">Rec: {pickup.accepted_at ? new Date(pickup.accepted_at).toLocaleString('es-CO', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</div>
                                            </td>
                                            <td className="px-4 py-3 align-top text-right">
                                                <div className="font-bold text-gray-900">{formatCurrency(pickup.amount)}</div>
                                                <div className="text-[10px] text-gray-500 mt-0.5">Factura: {formatCurrency(pickup.total_amount)}</div>
                                                {pickup.int_total_amount > 0 && (
                                                    <div className="text-[10px] text-purple-600 font-semibold">INT: {formatCurrency(pickup.int_total_amount)}</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 align-top text-xs text-gray-600">
                                                👤 {pickup.delivered_by || 'Bodega'}
                                            </td>
                                            <td className="px-4 py-3 align-top text-right">
                                                {pickup.has_rc ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-800 border border-green-200">
                                                        ✅ {pickup.rc_number?.name || pickup.rc_number}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-50 text-orange-700 border border-orange-100">
                                                        ⏳ Pendiente RC
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* PILAR 2: Recaudos Ruta (all non-bodega cash orders) — grouped by route */}
            {dailyIncome?.individual_collections && dailyIncome.individual_collections.length > 0 ? (() => {
                // Group orders by route_number
                const routeGroups = {};
                dailyIncome.individual_collections.forEach(item => {
                    const key = item.route_number ? `R${item.route_number}` : 'Sin Ruta';
                    if (!routeGroups[key]) {
                        routeGroups[key] = {
                            route_number: item.route_number,
                            route_date: item.route_date,
                            messenger_name: item.messenger_name,
                            orders: [],
                            total: 0
                        };
                    }
                    routeGroups[key].orders.push(item);
                    routeGroups[key].total += parseFloat(item.amount || 0);
                    if (item.route_date && !routeGroups[key].route_date) routeGroups[key].route_date = item.route_date;
                    if (item.messenger_name && !routeGroups[key].messenger_name) routeGroups[key].messenger_name = item.messenger_name;
                });
                const routeKeys = Object.keys(routeGroups);
                const hasMultipleRoutes = routeKeys.length > 1;
                let globalIdx = 0;

                return (
                    <Card className="mt-4 shadow-sm overflow-hidden">
                        <CardHeader className="bg-blue-50 pb-2 border-b border-blue-100">
                            <CardTitle className="text-lg text-blue-800 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span>🚚 PILAR 2: Recaudos de Ruta (Mensajeros)</span>
                                </div>
                                <div className="flex flex-col items-end">
                                    <span className="text-xs text-blue-500 uppercase font-bold tracking-wider">Total Ruta</span>
                                    <span className="text-xl font-black text-blue-900 leading-none">
                                        {formatCurrency(dailyIncome.individual_collections.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0))}
                                    </span>
                                </div>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            {/* Route Summary Strip — only when 2+ routes */}
                            {hasMultipleRoutes && (
                                <div className="flex flex-wrap gap-3 px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
                                    {routeKeys.map((key, ri) => {
                                        const g = routeGroups[key];
                                        return (
                                            <div key={key} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm border border-gray-100">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold ${g.route_number ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-600'}`}>
                                                    {key}
                                                </span>
                                                <div className="text-xs">
                                                    <div className="font-bold text-gray-900">{formatCurrency(g.total)}</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-gray-100 text-gray-600 border-b">
                                        <tr>
                                            <th className="px-4 py-2 font-medium w-12 text-center">#</th>
                                            <th className="px-4 py-2 font-medium">Ruta / Mensajero</th>
                                            <th className="px-4 py-2 font-medium">Pedido / INT</th>
                                            <th className="px-4 py-2 font-medium">Cliente</th>
                                            <th className="px-4 py-2 font-medium text-right">Valores</th>
                                            <th className="px-4 py-2 font-medium">Estado RC</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {routeKeys.map((key, ri) => {
                                            const g = routeGroups[key];
                                            const rows = [];
                                            g.orders.forEach((item) => {
                                                globalIdx++;
                                                rows.push(
                                                    <tr key={`order-${globalIdx}`} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-4 py-3 align-top text-center text-gray-400 font-mono text-xs">
                                                            {globalIdx.toString().padStart(2, '0')}
                                                        </td>
                                                        <td className="px-4 py-3 align-top">
                                                            <span className="font-bold text-indigo-800">{key}</span>
                                                            <div className="text-[10px] text-gray-500 font-medium">🛵 {item.delivered_by || item.messenger_name || 'Mensajero'}</div>
                                                        </td>
                                                        <td className="px-4 py-3 align-top">
                                                            <div className="font-bold text-gray-900">{item.order_number}</div>
                                                        </td>
                                                        <td className="px-4 py-3 align-top font-medium text-gray-800">
                                                            {item.customer_name}
                                                        </td>
                                                        <td className="px-4 py-3 align-top text-right">
                                                            <div className="font-bold text-gray-900">{formatCurrency(item.amount)}</div>
                                                        </td>
                                                        <td className="px-4 py-3 align-top text-right">
                                                            {item.has_rc ? (
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-800">
                                                                    ✅ {item.rc_number?.name || item.rc_number}
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-50 text-orange-700">
                                                                    ⏳ Pendiente
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            });
                                            return rows;
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                );
            })() : (
                <div className="mt-4 p-8 text-center text-gray-400 border-2 border-dashed rounded-lg">
                    🚚 Pilar 2: No hay recaudos de ruta registrados
                </div>
            )}

            {/* Empty global state if nothing at all */}
            {allCreditCustomerMovements.length === 0 && regularSales.length === 0 && (!dailyIncome?.individual_collections || dailyIncome.individual_collections.length === 0) && (
                <Card>
                    <CardContent className="text-center py-12 text-gray-400">
                        <div className="text-4xl mb-2">🔍</div>
                        No hay ingresos registrados en esta sesión
                    </CardContent>
                </Card>
            )}

            {/* Apply RC Modal */}
            {showRCModal && selectedOrder && (
                <ApplyRCModal
                    order={selectedOrder}
                    onClose={() => {
                        setShowRCModal(false);
                        setSelectedOrder(null);
                    }}
                    onSuccess={(data) => {
                        setShowRCModal(false);
                        setSelectedOrder(null);
                        // Refresh data to show updated RC status
                        fetchDailyIncome();
                    }}
                />
            )}
        </div>
    );
};

export default RouteReceptionTab;
